import { forwardRef, ReactNode, useRef, useState } from 'react';
import {
  Select as AriaSelect,
  SelectProps as AriaSelectProps,
  Button,
  ListBox,
  ListBoxItemProps,
  SelectValue,
  ValidationResult,
  ListBoxItem as AriaListBoxItem,
  composeRenderProps,
  Section,
  Header,
  Collection,
  SectionProps,
} from 'react-aria-components';
import { tv } from 'tailwind-variants';
import { composeTailwindRenderProps, focusRing } from 'src/utils';
import ExpandIcon from 'src/assets/icons/move-expand-vertical.svg';

import { Description, FieldError, Label } from './Field';
import TextField from './TextField';
import { Popover } from './Popover';

import type { ColorVariant, IconProps } from 'src/types/common';

export const selectStyles = tv({
  extend: focusRing,
  // border-transparent reserves the border box so toggling isInvalid's red
  // border causes no layout shift. Hover halo matches text fields — one
  // hover language across form controls (cross-cutting-023).
  base: 'flex items-center text-start font-semibold w-full border-2 border-transparent hover:shadow-[0_0_0_4px_var(--halo-hover)]',
  variants: {
    variant: {
      // Bordered white field per the current design system's <Select> —
      // the ghost chip predates it and leaves the control without a
      // perceivable boundary (~1.2:1).
      surface: 'bg-surface-alt text-surface-alt-90 border-surface-alt-60',
      primary:
        'bg-primary-ghost text-primary-ghost-75 hover:bg-primary-ghost-hover',
      secondary:
        'bg-secondary-ghost text-secondary-ghost-75 hover:bg-secondary-ghost-hover',
      success: 'bg-green-ghost text-green-ghost-75 hover:bg-green-ghost-hover',
      danger: 'bg-red-ghost text-red-ghost-75 hover:bg-red-ghost-hover',
      warning:
        'bg-yellow-ghost text-yellow-ghost-75 hover:bg-yellow-ghost-hover',
      ai: 'bg-ai-ghost text-ai-ghost-75 hover:bg-ai-ghost-hover',
    },
    size: {
      small: 'h-8 text-sm pl-3 pr-2 gap-2 rounded',
      medium: 'h-11 text-md pl-3.5 pr-2.5 gap-2.5 rounded-lg',
    },
    isDisabled: {
      true: 'opacity-75 cursor-not-allowed',
    },
    // Same invalid treatment as text fields — red border, red ring/halo.
    // The chip-era pink tint (31bc7a6) compensated for the missing border
    // and became redundant with the bordered restyle.
    isInvalid: {
      true: 'border-red outline-red [--focus-halo:var(--halo-red)]',
    },
  },
  defaultVariants: {
    variant: 'surface',
    size: 'medium',
  },
});

export const iconStyles = tv({
  base: '',
  variants: {
    variant: {
      // Darker than the ghost tint: on the bordered white field the chevron
      // is the open-indicator and needs 3:1 (design uses the #707070 family).
      surface: 'text-surface-alt-75',
      primary: 'text-primary-ghost-60',
      secondary: 'text-secondary-ghost-60',
      success: 'text-green-ghost-60',
      danger: 'text-red-ghost-60',
      warning: 'text-yellow-ghost-60',
      ai: 'text-ai-ghost-60',
    },
    size: {
      small: 'text-base',
      medium: 'text-xl',
    },
  },
  defaultVariants: {
    size: 'medium',
  },
});

export const dropdownItemStyles = tv({
  base: 'group flex justify-between items-center gap-2 cursor-pointer select-none py-2 pl-3 pr-1.5 rounded-lg outline outline-0 text-sm forced-color-adjust-none',
  variants: {
    isDisabled: {
      false: 'text-surface-ghost-75',
      true: 'text-surface-ghost-15 cursor-not-allowed',
    },
    isHovered: {
      true: 'bg-surface-alt-ghost',
      false: '',
    },
    // Keyboard-highlighted option — without this, arrow-key navigation
    // through an open menu has no visible indicator (WCAG 2.4.7).
    isFocused: {
      true: 'bg-secondary-ghost text-secondary-ghost-75',
    },
  },
});

type ItemsProps = {
  id?: string | number;
  name: string;
  description?: string;
  Icon?: IconProps;
};

export type SectionItemsProps = ItemsProps & {
  children?: ItemsProps[];
};

interface SelectItemProps extends ListBoxItemProps {
  IconLeft?: IconProps;
  IconRight?: IconProps;
}

const SelectItem = ({
  IconLeft,
  IconRight,
  textValue,
  children,
  ...props
}: SelectItemProps) => {
  const txtValue =
    textValue || (typeof children === 'string' ? children : undefined);
  return (
    <AriaListBoxItem
      {...props}
      textValue={txtValue}
      className={dropdownItemStyles}
    >
      {composeRenderProps(children, (childrenNode) => (
        <>
          {IconLeft ? (
            <IconLeft className="text-base text-surface-alt-ghost-45" />
          ) : null}
          <span className="flex-1 flex items-center justify-between gap-2 truncate text-sm font-semibold">
            {childrenNode}
          </span>
          {IconRight ? (
            <IconRight className="icon-right text-xs text-surface-alt-ghost-45" />
          ) : null}
        </>
      ))}
    </AriaListBoxItem>
  );
};

export interface DropdownSectionProps<T> extends SectionProps<T> {
  title?: string;
}

const SelectSection = <T extends object>({
  id,
  title,
  items,
  children,
}: DropdownSectionProps<T>) => {
  return (
    <Section
      id={id}
      className="first:-mt-1.5 after:content-[''] after:block after:h-1.5"
    >
      <Header className="text-sm text-surface-alt-75 uppercase font-semibold px-2 py-1.5 border-b border-surface-ghost -mx-1.5 mb-1.5">
        {title}
      </Header>
      <Collection items={items}>{children}</Collection>
    </Section>
  );
};

export interface SelectProps<T extends object> extends Omit<
  AriaSelectProps<T>,
  'children'
> {
  variant?: ColorVariant;
  size?: 'small' | 'medium';
  label?: string;
  description?: string;
  errorMessage?: string | ((validation: ValidationResult) => string);
  items?: Iterable<SectionItemsProps>;
  children?: ReactNode | ((item: T) => ReactNode);
  IconLeft?: IconProps;
  onSearch?: (value: any) => void;
  listLoading?: boolean;
  'data-rhf-name'?: string;
}

const Select = forwardRef<HTMLDivElement, SelectProps<object>>(
  (
    {
      variant = 'surface',
      size = 'medium',
      IconLeft,
      label,
      description,
      errorMessage,
      children,
      items,
      onSearch,
      onOpenChange,
      listLoading = false,
      'data-rhf-name': dataRhfName,
      ...props
    },
    ref
  ) => {
    const isFilterable = Boolean(onSearch);
    const [inputValue, setInputValue] = useState('');
    const searchRef = useRef<HTMLInputElement>(null);

    return (
      <AriaSelect
        ref={ref}
        {...props}
        onOpenChange={(isOpen) => {
          if (!isOpen) {
            setInputValue('');
            onSearch?.('');
          }
          if (isOpen && isFilterable)
            setTimeout(() => searchRef.current?.focus(), 0);
          onOpenChange?.(isOpen);
        }}
        className={composeTailwindRenderProps(
          props.className,
          'group flex flex-col gap-1'
        )}
      >
        {label && <Label isRequired={props.isRequired}>{label}</Label>}
        <Button
          data-rhf-name={dataRhfName}
          className={({ isFocusVisible }) =>
            selectStyles({
              variant,
              size,
              isDisabled: props.isDisabled,
              isInvalid: props.isInvalid,
              isFocusVisible,
            })
          }
        >
          {IconLeft ? (
            <IconLeft
              className={`${size === 'small' ? 'w-4 h-4' : 'w-5 h-5'} text-surface-alt-ghost-45`}
            />
          ) : null}
          <SelectValue className="flex-1 flex items-center gap-2.5 text-inherit [&>.icon-right]:hidden max-w-full" />
          <ExpandIcon
            aria-hidden
            className={`opacity-85 ${iconStyles({ variant, size })}`}
          />
        </Button>
        {description && <Description>{description}</Description>}
        <FieldError>{errorMessage}</FieldError>
        <Popover
          className={`min-w-[--trigger-width] ${isFilterable ? 'mb-14' : ''}`}
          // shouldCloseOnInteractOutside={() => !isFilterable}
        >
          {isFilterable && (
            <TextField
              ref={searchRef}
              size="small"
              value={inputValue}
              onChange={(value) => {
                setInputValue(value);
                if (onSearch) onSearch(value);
              }}
              className="mx-4 mt-4 mb-1"
              placeholder="Type at least 3 characters..."
              aria-label="Search select options"
            />
          )}
          {listLoading ? (
            <div
              role="status"
              aria-live="polite"
              className="p-2 text-center text-sm text-surface-alt-75 font-semibold"
            >
              Loading...
            </div>
          ) : (
            <ListBox
              items={items}
              className="bg-surface-alt outline-none p-1.5 max-h-[inherit] overflow-auto [clip-path:inset(0_0_0_0_round_.75rem)]"
            >
              {(section) => {
                if (section?.children?.length) {
                  return (
                    <SelectSection
                      id={section.name}
                      title={section.name}
                      items={section.children}
                    >
                      {(item) => (
                        <SelectItem
                          key={item?.id ?? item.name}
                          id={item?.id ?? item.name}
                          textValue={item.name}
                        >
                          {item.name}
                        </SelectItem>
                      )}
                    </SelectSection>
                  );
                }

                return (
                  <SelectItem
                    key={section?.id ?? section.name}
                    id={section?.id ?? section.name}
                    textValue={section.name}
                  >
                    {section?.Icon && (
                      <section.Icon className="w-4 h-4 text-[#999999]" />
                    )}
                    {section?.description ? (
                      <span className="flex flex-1 flex-col max-w-full">
                        <span className="truncate">{section.name}</span>
                        <span className="font-medium text-xs text-surface-alt-ghost-75">
                          {section.description}
                        </span>
                      </span>
                    ) : (
                      <span className={section?.Icon ? 'flex-1' : ''}>
                        {section.name}
                      </span>
                    )}
                  </SelectItem>
                );
              }}
            </ListBox>
          )}
        </Popover>
      </AriaSelect>
    );
  }
);

Select.displayName = 'Select';

export default Select;
